/**
 * Aufbau des E-Mail-Texts beim Schreiben – gemeinsam für „Neue Nachricht“,
 * das Antwortfeld in der Korrespondenz und das schwebende
 * Unterhaltungs-Fenster (Kundenfeedback 25.10.2026 Teil 3, I: „Es wäre
 * praktisch, wenn einfach immer alles gleich abläuft, egal ob ich eine neue
 * E-Mail erstelle, antworte, weiterleite …“).
 *
 * Reihenfolge wie in Outlook (Kundenfeedback 14.09.26):
 *   eigener Text / eingefügte Vorlagen
 *   Signatur            (data-signature="true")
 *   zitierter Verlauf   (data-quoted="true")
 *
 * Bewusst stringbasiert (kein DOM): läuft identisch im Browser und in den
 * Node-Tests. Die Aufrufer setzen das Ergebnis als innerHTML des Editors.
 */
import { quotedHistoryBlockHtml, signatureBlockHtml } from '@/utils/signatureTemplate'

export const SIGNATURE_ATTR = 'data-signature="true"'
export const QUOTED_ATTR = 'data-quoted="true"'
export const TEMPLATE_ATTR = 'data-template="true"'

/** Klasse der markierten, nicht befüllbaren Vorlagen-Platzhalter (utils/mailTemplate). */
const MISSING_PLACEHOLDER = /<span\b[^>]*class="[^"]*\bmail-tpl-missing\b[^"]*"[^>]*>([\s\S]*?)<\/span>/gi
const TRAILING_BREAKS = /(?:\s|<br\s*\/?>)+$/i
/** Übliche Grußformeln am Briefende („Mit freundlichen Grüßen“, „Beste Grüße“ …). */
const CLOSING = /(?:mit\s+(?:freundlichen|besten|kollegialen|herzlichen)|freundliche|beste|viele|herzliche|kollegiale|liebe)\s+grüße?n?/i
/** Nach dem Schlussgruß der Vorlage darf nur noch kurzer Text (Name, Funktion) stehen. */
const MAX_TEXT_AFTER_CLOSING = 120
/** Öffnende Tags direkt vor dem Gruß gehören zu dessen Zeile (<p>, <div> …). */
const OPENING_TAGS_BEFORE = /(?:<(?!\/)[a-z][a-z0-9]*\b[^>]*>)+$/i

interface Span {
  start: number
  end: number
}

function escapeRegExp (value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)
}

/**
 * Erstes Element mit dem Attribut samt schließendem Tag. Gleichnamige
 * verschachtelte Elemente werden mitgezählt – Signaturen bestehen oft selbst
 * aus <div>s, ein nicht-gieriger Ausdruck würde mitten darin abschneiden.
 */
export function findMarkedElement (html: string, attr: string): Span | null {
  const open = new RegExp(String.raw`<([a-z][a-z0-9]*)\b[^>]*\s${escapeRegExp(attr)}[^>]*>`, 'i').exec(html)
  if (!open) {
    return null
  }
  const tag = (open[1] as string).toLowerCase()
  const tags = new RegExp(String.raw`<(/?)${tag}\b[^>]*>`, 'gi')
  tags.lastIndex = open.index + open[0].length
  let depth = 1
  for (let t = tags.exec(html); t; t = tags.exec(html)) {
    if (t[0].endsWith('/>')) {
      continue
    }
    depth += t[1] ? -1 : 1
    if (depth === 0) {
      return { start: open.index, end: t.index + t[0].length }
    }
  }
  // Nicht geschlossen (abgeschnittenes Markup): bis zum Ende.
  return { start: open.index, end: html.length }
}

/** Index vor den Leerzeilen (<br>), die einen Block vom Text davor trennen. */
function startOfLeadingBreaks (html: string, index: number): number {
  const before = html.slice(0, index)
  const m = TRAILING_BREAKS.exec(before)
  return m ? m.index : index
}

/** Sichtbarer Text ohne Markup (Leerraum zusammengefasst). */
export function plainText (html: string): string {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function hasSignature (html: string): boolean {
  return findMarkedElement(html || '', SIGNATURE_ATTR) !== null
}

export function hasQuotedHistory (html: string): boolean {
  return findMarkedElement(html || '', QUOTED_ATTR) !== null
}

/** Alle Blöcke mit dem Attribut entfernen (samt der trennenden Leerzeilen davor). */
function removeAll (html: string, attr: string): string {
  let out = html || ''
  for (let span = findMarkedElement(out, attr); span; span = findMarkedElement(out, attr)) {
    out = out.slice(0, startOfLeadingBreaks(out, span.start)) + out.slice(span.end)
  }
  return out
}

export function removeSignature (html: string): string {
  return removeAll(html, SIGNATURE_ATTR)
}

/**
 * Signatur setzen bzw. austauschen (Standortwechsel, „Signatur bearbeiten“).
 * Eine vorhandene Signatur wird ersetzt – nie eine zweite angehängt. Neu
 * eingefügt steht sie vor dem zitierten Verlauf. Leere Signatur = entfernen.
 */
export function setSignature (html: string, signature: string): string {
  const src = html || ''
  if (!signature) {
    return removeSignature(src)
  }
  const existing = findMarkedElement(src, SIGNATURE_ATTR)
  if (existing) {
    const block = signatureBlockHtml(signature).replace(/^(?:<br>)+/, '')
    return src.slice(0, existing.start) + block + src.slice(existing.end)
  }
  const quoted = findMarkedElement(src, QUOTED_ATTR)
  if (quoted) {
    const at = startOfLeadingBreaks(src, quoted.start)
    return src.slice(0, at) + signatureBlockHtml(signature) + src.slice(at)
  }
  return src + signatureBlockHtml(signature)
}

/**
 * Schlussgruß am Ende einer Vorlage („Mit freundlichen Grüßen / Name“)
 * entfernen. Nur, wenn danach höchstens noch kurzer Text folgt – sonst
 * gehört der Gruß nicht zum Schluss der Vorlage.
 */
function removeTrailingClosing (html: string): string {
  const pattern = new RegExp(CLOSING.source, 'gi')
  let last: RegExpExecArray | null = null
  for (let m = pattern.exec(html); m; m = pattern.exec(html)) {
    last = m
  }
  if (!last || plainText(html.slice(last.index + last[0].length)).length > MAX_TEXT_AFTER_CLOSING) {
    return html
  }
  const before = html.slice(0, last.index)
  const tags = OPENING_TAGS_BEFORE.exec(before)
  return (tags ? before.slice(0, tags.index) : before).replace(TRAILING_BREAKS, '')
}

/**
 * Text einer Vorlage einfügen: oberhalb der Signatur bzw. des zitierten
 * Verlaufs, sonst ans Ende. Bringt die Vorlage selbst eine Signatur mit
 * (z. B. aus einem Entwurf gespeichert), wird diese entfernt – die Signatur
 * steht genau einmal im Text (Kundenfeedback 25.10.2026 Teil 3, I). Grüßt
 * die aktive Signatur selbst, entfällt auch der Schlussgruß der Vorlage –
 * sonst stünde „Mit freundlichen Grüßen“ zweimal untereinander.
 */
export function insertTemplate (html: string, templateHtml: string): string {
  const src = html || ''
  let body = removeAll(removeSignature(templateHtml || ''), QUOTED_ATTR).replace(TRAILING_BREAKS, '')
  const signature = findMarkedElement(src, SIGNATURE_ATTR)
  if (signature && CLOSING.test(plainText(src.slice(signature.start, signature.end)))) {
    body = removeTrailingClosing(body)
  }
  if (!plainText(body) && !/<img\b/i.test(body)) {
    return src
  }
  const block = `<div ${TEMPLATE_ATTR}>${body}</div>`
  const anchorSpan = findMarkedElement(src, SIGNATURE_ATTR) ?? findMarkedElement(src, QUOTED_ATTR)
  if (anchorSpan) {
    const at = startOfLeadingBreaks(src, anchorSpan.start)
    const before = src.slice(0, at)
    // Leerzeilen zwischen eigenem Text und Signatur bleiben erhalten.
    return before + (plainText(before) ? '<br>' : '') + block + src.slice(at)
  }
  return plainText(src) ? `${src}<br>${block}` : block
}

/**
 * Zitierten Verlauf (Antworten/Weiterleiten) hinter die Signatur setzen.
 * Steht schon ein Verlauf im Text, bleibt es bei diesem einen.
 */
export function insertQuotedHistory (html: string, quotedHtml: string): string {
  const src = html || ''
  if (!quotedHtml || hasQuotedHistory(src)) {
    return src
  }
  const block = quotedHistoryBlockHtml(quotedHtml)
  const signature = findMarkedElement(src, SIGNATURE_ATTR)
  if (signature) {
    return src.slice(0, signature.end) + block + src.slice(signature.end)
  }
  return src + block
}

/**
 * Vorbelegung beim Öffnen: Signatur und zitierter Verlauf – in genau der
 * Reihenfolge, die auch „Neue Nachricht“ verwendet.
 */
export function initialBody (signature: string, quotedHtml = ''): string {
  return insertQuotedHistory(signature ? signatureBlockHtml(signature) : '', quotedHtml)
}

/**
 * Eigener Text ohne Signatur und zitierten Verlauf. Die Vorbelegung allein
 * macht eine Nachricht weder sendbar noch zum Entwurf (Kundenfeedback 14.09.26).
 */
export function ownText (html: string): string {
  return plainText(removeAll(removeSignature(html), QUOTED_ATTR))
}

/**
 * Text für „Als Vorlage speichern“: ohne Signatur und zitierten Verlauf;
 * markierte Platzhalter werden wieder zu {{schlüssel}}, Vorlagen-Hüllen
 * aufgelöst.
 */
export function bodyForTemplate (html: string): string {
  let out = removeAll(removeSignature(html), QUOTED_ATTR).replace(MISSING_PLACEHOLDER, '$1')
  for (let span = findMarkedElement(out, TEMPLATE_ATTR); span; span = findMarkedElement(out, TEMPLATE_ATTR)) {
    const element = out.slice(span.start, span.end)
    const inner = element.slice(element.indexOf('>') + 1, element.lastIndexOf('</'))
    out = out.slice(0, span.start) + inner + out.slice(span.end)
  }
  return out.replace(TRAILING_BREAKS, '')
}
